import { useEffect, useMemo, useRef, useState } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { useDiagramDocument } from '../contexts/DiagramDocumentContext';
import { createDiagramProjection } from '../lib/diagram/tanstackProjection';
import { debounce } from '../utils/debounce';
import type { Node as DiagramNode } from '../core/models/scene';
import { YjsAwarenessBroadcaster } from '../lib/diagram/YjsAwarenessBroadcaster';
import * as Y from 'yjs';
import { Logger } from '@/utils/logger';
import { markNodeDirty, getGlobalFlushLayoutAndClearDirty } from '../domains/orgchart/hooks/useAutoLayout';
import {
  buildFiniteGeometryPatch,
  valueChanged,
} from '../lib/diagram/realtimeShapePatches';

const logger = Logger.getLoggerWithPrefix('useDiagramRealtimeWithProjection');

const STRUCTURAL_NODE_KEYS = new Set([
  'id',
  'type',
  'category',
  'x',
  'y',
  'w',
  'h',
  'width',
  'height',
  'zIndex',
  'rotation',
  'style',
  'props',
]);

function buildShapePropsFromNode(node: DiagramNode): Record<string, unknown> {
  const props =
    node.props && typeof node.props === 'object'
      ? { ...(node.props as Record<string, unknown>) }
      : {};

  Object.entries(node as unknown as Record<string, unknown>).forEach(([key, value]) => {
    if (!STRUCTURAL_NODE_KEYS.has(key)) {
      props[key] = value;
    }
  });

  if (node.attrs && typeof node.attrs === 'object') {
    props.attrs = { ...(node.attrs as Record<string, unknown>) };
  }

  return props;
}

interface UseDiagramRealtimeOptions {
  diagramType: 'genogram' | 'flowchart' | 'mindmap' | 'network' | 'org' | 'database' | 'uml' | 'generic';
  /**
   * When `false` the hook becomes a no-op: no Yjs projection is created,
   * no Zustand→Yjs writebacks fire, no awareness broadcaster spins up.
   *
   * Used by Compass Explorer diagrams: the Explorer synthesises its scene
   * client-side from the folder knowledge graph (see
   * `useCompassExplorerSync`) and the Yjs doc is deliberately kept empty.
   * Running the projection in parallel would race against
   * `diagramStore.loadScene(...)` and occasionally blank the canvas.
   * Defaults to `true` for every other diagram type.
   */
  enabled?: boolean;
}

export function useDiagramRealtimeWithProjection({ diagramType, enabled = true }: UseDiagramRealtimeOptions) {
  const { batchAddNodes, updateNodes, deleteNode, batchAddEdges, updateEdges, deleteEdge, setInitialLoading } = useDiagramStore();
  const projectionRef = useRef<ReturnType<typeof createDiagramProjection> | null>(null);
  const yjsBroadcasterRef = useRef<YjsAwarenessBroadcaster | null>(null);
  const isSyncingFromYjs = useRef(false);
  const isSyncingToYjs = useRef(false);
  const ephemeralPreviewIdsRef = useRef<Set<string>>(new Set());
  const pendingPropertyIdsRef = useRef<Set<string>>(new Set());
  const hasInitialSyncCompleted = useRef(false);
  const initialSyncTimestamp = useRef<number>(0);
  const positionReconciled = useRef(false);
  const processedUpdateTimestamps = useRef<Set<number>>(new Set()); // Track processed Yjs updates

  // Get document from context
  const { doc, awareness, provider, isSynced, syncMode } = useDiagramDocument();

  // State for projection data
  const [shapes, setShapes] = useState<Map<string, any>>(new Map());
  const [connectors, setConnectors] = useState<Map<string, any>>(new Map());

  // Set up TanStack DB projection (WAIT for snapshot to load first)
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!doc || !isSynced) {
      return;
    }

    // Only create projection once per document
    // Don't recreate when isSynced changes from false→true
    if (projectionRef.current) {
      logger.info('Projection already exists, skipping recreation');
      return;
    }

    logger.info('Creating projection for first time');
    const projection = createDiagramProjection(doc);
    projectionRef.current = projection;

    // Load initial state immediately (before subscription)
    const initialState = projection.store.getState();
    setShapes(new Map(initialState.shapes));
    setConnectors(new Map(initialState.connectors));
    try {
      logger.debug('Debug SDI: Projection created in useDiagramRealtimeWithProjection', {
        shapes: initialState.shapes.size,
        connectors: initialState.connectors.size
      });
    } catch {}

    // Subscribe to store changes
    const unsubscribe = projection.store.subscribe((state) => {
      setShapes(new Map(state.shapes));
      setConnectors(new Map(state.connectors));
    });

    return () => {
      unsubscribe();
      projection.cleanup();
      projectionRef.current = null;
    };
  }, [doc, isSynced, enabled]);

  // L5: Reset processed timestamps when doc changes (diagram navigation)
  useEffect(() => {
    processedUpdateTimestamps.current = new Set();
  }, [doc]);

  // Set up Yjs-level awareness broadcasting (CLEAN APPROACH)
  // All commands → Yjs → broadcast via awareness → other clients apply to Yjs → projection syncs to store
  // C1: Only create broadcaster in collab mode — solo mode has no remote peers to notify (§6.11)
  useEffect(() => {
    if (!doc || !awareness || !isSynced) return;
    if (syncMode !== 'collab') {
      // Solo mode: no broadcaster needed. Clean up if one was previously active.
      if (yjsBroadcasterRef.current) {
        yjsBroadcasterRef.current.destroy();
        yjsBroadcasterRef.current = null;
      }
      return;
    }

    // Create broadcaster - automatically listens to Yjs updates and broadcasts them
    const broadcaster = new YjsAwarenessBroadcaster(doc, awareness);
    yjsBroadcasterRef.current = broadcaster;

    // Track per-client yjsUpdateQueue summary to detect real queue changes
    const lastQueueSummaryRef = new Map<number, { len: number; lastTs: number }>();

    // Listen for awareness updates - apply remote Yjs updates directly to our doc
    const awarenessChangeHandler = ({ added, updated }: { added: number[]; updated: number[]; removed: number[] }) => {
      // Fast path: only proceed if any changed client has a non-empty yjsUpdateQueue that appears newer than what we've seen
      let queuesChanged = false;
      const changed = [...(added || []), ...(updated || [])];
      const states = awareness.getStates();

      for (const clientId of changed) {
        const state: any = states.get(clientId);
        const q: any[] | undefined = state?.yjsUpdateQueue;
        if (!Array.isArray(q) || q.length === 0) {
          continue; // nothing to apply from this client
        }
        const lastItemTs = typeof q[q.length - 1]?.timestamp === 'number' ? q[q.length - 1].timestamp : 0;
        const prev = lastQueueSummaryRef.get(clientId);
        if (!prev || prev.len !== q.length || lastItemTs > prev.lastTs) {
          lastQueueSummaryRef.set(clientId, { len: q.length, lastTs: lastItemTs });
          queuesChanged = true;
        }
      }

      if (!queuesChanged) return; // Ignore awareness changes unrelated to yjsUpdateQueue

      const remoteUpdates = broadcaster.getRemoteUpdates();

      if (remoteUpdates.length === 0) return;

      // Apply each remote update directly to our Yjs doc
      for (const update of remoteUpdates) {
        if (processedUpdateTimestamps.current.has(update.timestamp)) continue;
        processedUpdateTimestamps.current.add(update.timestamp);
        Y.applyUpdate(doc, update.data, 'awareness');
      }

      // Clean up old timestamps (keep last 1000)
      if (processedUpdateTimestamps.current.size > 1000) {
        const timestamps = Array.from(processedUpdateTimestamps.current);
        processedUpdateTimestamps.current = new Set(timestamps.slice(-500));
      }
    };

    awareness.on('update', awarenessChangeHandler);

    return () => {
      awareness.off('update', awarenessChangeHandler as any);
      broadcaster.destroy();
      yjsBroadcasterRef.current = null;
    };
  }, [doc, awareness, isSynced, syncMode]);

  // Track which shapes and connectors we've seen
  const syncedShapeIds = useRef(new Set<string>());
  const syncedConnectorIds = useRef(new Set<string>());
  // Track previous shape/connector references to detect which actually changed
  const prevShapeRefsRef = useRef<Map<string, any>>(new Map());
  const prevConnectorRefsRef = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    if (!shapes || !connectors) {
      return;
    }
    if (isSyncingToYjs.current) {
      return; // Prevent feedback loop
    }

    isSyncingFromYjs.current = true;

    // Only log occasionally or when shape count changes to avoid logger spam
    const prevShapeCount = syncedShapeIds.current.size;
    if (shapes.size !== prevShapeCount || Math.random() < 0.02) {
      logger.info('[Projection→Store] Syncing from Yjs:', {
        shapesCount: shapes.size,
        connectorsCount: connectors.size,
        shapeIds: Array.from(shapes.keys()).slice(0, 5) // Only show first 5 IDs
      });
    }

    // Track current IDs
    const currentShapeIds = new Set<string>();
    const currentConnectorIds = new Set<string>();

    // Helper: check if there is an active remote preview for a shape id
    const hasRemotePreview = (shapeId: string): boolean => {
      if (!awareness) return false;
      const entries = Array.from(awareness.getStates().entries()) as any[];
      // Accept preview from any client (including same user in another tab)
      for (const [, state] of entries) {
        const previews = state?.previews as Record<string, any> | undefined;
        if (previews && previews[shapeId]) return true;
      }
      return false;
    };

    // Handle shapes
    const shallowEqual = (a: any, b: any) => {
      if (a === b) return true;
      if (!a || !b) return false;
      const ak = Object.keys(a);
      const bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      for (const k of ak) {
        if (!Object.is(a[k], b[k])) return false;
      }
      return true;
    };

    // Build O(1) lookup map for current store state
    const nodeById = new Map(useDiagramStore.getState().nodes.map(n => [n.id, n]));

    // Determine which shapes actually changed since last sync cycle.
    // The projection fires on any Yjs change with ALL shapes, but only changed
    // shape objects have new references.
    const prevRefs = prevShapeRefsRef.current;
    const changedShapeIds = new Set<string>();
    shapes.forEach((shape, id) => {
      currentShapeIds.add(id);
      if (prevRefs.get(id) !== shape || !syncedShapeIds.current.has(id)) {
        changedShapeIds.add(id);
      }
    });
    prevShapeRefsRef.current = new Map(shapes);

    // Collect batch operations instead of individual store mutations
    const nodesToAdd: any[] = [];
    const nodesToUpdate: Array<{ id: string; updates: any }> = [];

    changedShapeIds.forEach((shapeId) => {
      const shape = shapes.get(shapeId)!

      try {
        // Prepare props with defensive fallback for transient records
        let shapeProps: any = shape.props;
        if (shape.type === 'freehand') {
          // Freehand may arrive with points either at props.attrs.points (correct)
          // or at props.points (older buggy writer). Normalize here.
          const propsAttrs = (shape as any)?.props?.attrs;
          const hasPropsPoints = shapeProps && typeof (shapeProps as any).points !== 'undefined';
          const hasAttrsPoints = propsAttrs && typeof (propsAttrs as any).points !== 'undefined';
          if (!hasAttrsPoints && hasPropsPoints) {
            shapeProps = { ...(shapeProps as any) };
          } else if (!shapeProps || (!hasPropsPoints && !hasAttrsPoints)) {
            shapeProps = {
              points: [],
              strokeColor: shape.style?.strokeColor,
              strokeWidth: (shape.style as any)?.strokeWidth,
              opacity: (shape.style as any)?.opacity,
            };
          }
        }

        // Shape data is stored directly on the shape object, not in a nested props field
        // Merge attrs from both sources so new-format updates override old-format values.
        const shapeAttrs = {
          ...((shape as any).attrs || {}),
          ...((shape as any)?.props?.attrs || {}),
        };

        const mergedAttrs: any = { ...shapeAttrs };
        if (shape.type === 'freehand') {
          const candidate = (shape as any)?.props?.attrs || shapeProps || {};
          const keys = ['points', 'strokeColor', 'strokeWidth', 'opacity', 'lineCap', 'lineJoin', 'tension'];
          for (const k of keys) {
            const v = (candidate as any)?.[k];
            if (typeof v !== 'undefined' && typeof (mergedAttrs as any)[k] === 'undefined') {
              (mergedAttrs as any)[k] = v;
            }
          }
        }

        const deceasedFlag =
          (shape as any).deceased ?? shapeProps?.deceased ?? (shapeAttrs as any)?.isDeceased ?? (shapeAttrs as any)?.deceased ?? false;

        const nameToCheck = (shape as any).name || shapeProps?.name;
        if (nameToCheck === 'Helen Anderson' || nameToCheck === 'Michael Anderson') {
          logger.info('[Projection→Store] Converting shape to nodeData:', {
            id: shape.id,
            name: nameToCheck,
            shapePropsKeys: Object.keys(shapeProps || {}),
            hasMedications: 'medications' in (shapeProps || {}),
            medications: (shapeProps as any)?.medications,
            hasGeneticMarkers: 'geneticMarkers' in (shapeProps || {}),
            geneticMarkers: (shapeProps as any)?.geneticMarkers
          });
        }

        const nodeData = {
          id: shape.id,
          type: shape.type || 'person',
          props: shapeProps,
          ...(shapeProps || {}),
          ...((shape as any).name ? { name: (shape as any).name } : {}),
          ...((shape as any).sex ? { sex: (shape as any).sex } : {}),
          ...((shape as any).text ? { text: (shape as any).text } : {}),
          ...((shape as any).fontSize ? { fontSize: (shape as any).fontSize } : {}),
          ...((shape as any).fontFamily ? { fontFamily: (shape as any).fontFamily } : {}),
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
          w: shape.width,
          h: shape.height,
          zIndex: (shape as any).zIndex,
          fillColor: shape.style?.fillColor || (shape as any).fillColor || shapeProps?.fillColor,
          strokeColor: shape.style?.strokeColor || (shape as any).strokeColor || shapeProps?.strokeColor,
          deceased: Boolean(deceasedFlag),
          locked: Boolean((shape as any).locked ?? shapeProps?.locked ?? (shapeAttrs as any)?.locked ?? false),
          attrs: {
            ...mergedAttrs,
            rotation: shape.rotation ?? (mergedAttrs as any).rotation
          },
          _optimistic: undefined,
        } as any;

        const isAlreadySynced = syncedShapeIds.current.has(shape.id);

        if (!isAlreadySynced) {
          nodesToAdd.push(nodeData);
          syncedShapeIds.current.add(shape.id);
        } else {
          const shouldOverwriteAttrs = !pendingPropertyIdsRef.current.has(shape.id);
          const current = nodeById.get(shape.id) as any;

          if (!current) {
            nodesToAdd.push(nodeData);
            return;
          }

          const prevAttrs = (current?.attrs) || {};
          const incoming = nodeData.attrs || {};
          const mergedAttrsForUpdate = shouldOverwriteAttrs ? { ...incoming } : undefined;

          const collectIfChanged = (updates: any) => {
            let changed = false;
            for (const k of Object.keys(updates)) {
              if (k === 'attrs') continue;
              const currentVal = (current as any)[k];
              const updateVal = updates[k];
              if (!Object.is(currentVal, updateVal)) {
                changed = true;
                break;
              }
            }
            if (!changed && 'attrs' in updates) {
              const nextAttrs = updates.attrs as any;
              if (!shallowEqual(prevAttrs, nextAttrs)) changed = true;
            }
            if (changed) {
              nodesToUpdate.push({ id: shape.id, updates });
            }
          };

          if (hasRemotePreview(shape.id)) {
            ephemeralPreviewIdsRef.current.add(shape.id);
            const updates: any = {
              type: nodeData.type,
              name: nodeData.name,
              sex: nodeData.sex,
              deceased: nodeData.deceased,
              locked: nodeData.locked,
              text: nodeData.text,
              fontSize: nodeData.fontSize,
              fontFamily: nodeData.fontFamily,
              zIndex: nodeData.zIndex,
              fillColor: nodeData.fillColor,
            };
            if (shouldOverwriteAttrs) updates.attrs = mergedAttrsForUpdate;
            collectIfChanged(updates);
          } else {
            ephemeralPreviewIdsRef.current.delete(shape.id);
            const updates: any = {
              type: nodeData.type,
              name: nodeData.name,
              sex: nodeData.sex,
              deceased: nodeData.deceased,
              locked: nodeData.locked,
              text: nodeData.text,
              fontSize: nodeData.fontSize,
              fontFamily: nodeData.fontFamily,
              x: nodeData.x,
              y: nodeData.y,
              w: nodeData.w,
              h: nodeData.h,
              width: nodeData.width || nodeData.w,
              height: nodeData.height || nodeData.h,
              zIndex: nodeData.zIndex,
              fillColor: nodeData.fillColor,
              strokeColor: nodeData.strokeColor,
            };
            if (shouldOverwriteAttrs) updates.attrs = mergedAttrsForUpdate;

            // No value stripping — let ALL Yjs values flow to the store so
            // the latest height, width, position, expanded state, etc. are
            // applied immediately.  The auto-layout trigger is suppressed
            // separately below (during the settling window) to prevent the
            // height delta (e.g. 72→94 from rendered height) from causing a
            // full re-layout on load.

            if (current && (current.x !== nodeData.x || current.y !== nodeData.y)) {
              logger.info('[Yjs→Store] Shape moved:', {
                id: shape.id,
                from: { x: current.x, y: current.y },
                to: { x: nodeData.x, y: nodeData.y }
              });
            }

            if (current && (current.fillColor !== nodeData.fillColor || current.strokeColor !== nodeData.strokeColor)) {
              logger.info('[Yjs→Store] Color changed:', {
                id: shape.id,
                from: { fillColor: current.fillColor, strokeColor: current.strokeColor },
                to: { fillColor: nodeData.fillColor, strokeColor: nodeData.strokeColor },
                shouldOverwriteAttrs,
                isPending: pendingPropertyIdsRef.current.has(shape.id)
              });
            }

            collectIfChanged(updates);
          }
        }
      } catch (err) {
        logger.error('Invalid shape:', shape.id, err);
      }
    });

    // Batch apply all node additions and updates (1-2 mutations instead of N)
    if (nodesToAdd.length > 0) {
      batchAddNodes(nodesToAdd, true);
    }
    if (nodesToUpdate.length > 0) {
      updateNodes(nodesToUpdate, true);
    }

    // After store has new type/dimensions, trigger org layout so it runs with fresh data (avoids gap when switching to "no image")
    const LAYOUT_KEYS = ['type', 'width', 'height', 'w', 'h'];
    const hasLayoutAffectingUpdates =
      diagramType === 'org' &&
      nodesToUpdate.some((u) => LAYOUT_KEYS.some((k) => k in (u.updates || {})));
    // During the initial settling window, suppress auto-layout triggers.
    // The bootstrap→Electric height delta (e.g. 72→94) would otherwise mark
    // every node dirty and cause a full re-layout on load.  User-initiated
    // dimension changes (type switch, expand/collapse) arrive via HTTP
    // catch-up and are already in the bootstrap, so they don't need layout.
    const SETTLE_MS = 3000;
    const isSettling = initialSyncTimestamp.current > 0
      && (Date.now() - initialSyncTimestamp.current) < SETTLE_MS;

    if (hasLayoutAffectingUpdates && !isSettling) {
      nodesToUpdate.forEach(({ id }) => markNodeDirty(id));
      getGlobalFlushLayoutAndClearDirty()?.();
    }

    // Sync tick summary
    try {
      logger.debug('Debug SDI: Yjs→Store sync tick', {
        shapes: shapes.size,
        connectors: connectors.size,
        isSynced
      });
    } catch {}

    // Handle deleted shapes
    if (isSynced) {
      syncedShapeIds.current.forEach(id => {
        if (!currentShapeIds.has(id)) {
          logger.info('[Yjs→Store] DELETE shape:', { id });
          try { logger.debug('Debug SDI: Deleting shape during ingest', { id }); } catch {}
          deleteNode(id, true);
          syncedShapeIds.current.delete(id);
        }
      });
    }

    const draggingIds = useDiagramStore.getState().draggingIds;

    // Determine which connectors actually changed since last sync (ref-based, like shapes).
    const prevConnectorRefs = prevConnectorRefsRef.current;
    const changedConnectorIds = new Set<string>();
    connectors.forEach((connector) => {
      currentConnectorIds.add(connector.id);
      if (prevConnectorRefs.get(connector.id) !== connector || !syncedConnectorIds.current.has(connector.id)) {
        changedConnectorIds.add(connector.id);
      }
    });
    prevConnectorRefsRef.current = new Map(connectors);

    // Collect batch operations for connectors — only process changed ones
    const edgesToAdd: any[] = [];
    const edgesToUpdate: Array<{ id: string; updates: any }> = [];

    changedConnectorIds.forEach((connectorId) => {
      const connector = connectors.get(connectorId)!;

      try {
        if (draggingIds.has(connector.id)) {
          return;
        }

        const props = connector.props;
        const hasNestedAttrs = props && typeof props === 'object' && 'attrs' in props;

        const rawAttrs = hasNestedAttrs
          ? (props.attrs || {})
          : (() => {
              const fromProps = (props && typeof props === 'object') ? ({ ...(props as Record<string, unknown>) }) : {};
              delete (fromProps as any).fromAnchor;
              delete (fromProps as any).toAnchor;
              delete (fromProps as any).fromOffset;
              delete (fromProps as any).toOffset;
              delete (fromProps as any).sourceAnchor;
              delete (fromProps as any).targetAnchor;
              delete (fromProps as any).sourceOffset;
              delete (fromProps as any).targetOffset;
              return Object.keys(fromProps).length > 0 ? fromProps : ((connector as any).attrs || {});
            })();

        const connectorAttrs = {
          ...rawAttrs,
          ...(((connector as any).routingType && !(rawAttrs as any)?.routingType)
            ? { routingType: (connector as any).routingType }
            : {}),
        };

        const fromId = connector.sourceShapeId || (connector as any).source || (connector as any).from;
        const toId = connector.targetShapeId || (connector as any).target || (connector as any).to;

        const edgeData = {
          id: connector.id,
          type: connector.type,
          from: fromId,
          to: toId,
          attrs: connectorAttrs,
          fromAnchor: (connector as any).fromAnchor || connector.props?.fromAnchor,
          toAnchor: (connector as any).toAnchor || connector.props?.toAnchor,
          fromOffset: (connector as any).fromOffset || connector.props?.fromOffset,
          toOffset: (connector as any).toOffset || connector.props?.toOffset,
          fromAnchorLocked: (connector as any).fromAnchorLocked ?? (connector.props as any)?.fromAnchorLocked,
          toAnchorLocked: (connector as any).toAnchorLocked ?? (connector.props as any)?.toAnchorLocked,
          _optimistic: undefined,
        } as any;

        if (!syncedConnectorIds.current.has(connector.id)) {
          edgesToAdd.push(edgeData);
          syncedConnectorIds.current.add(connector.id);
        } else {
          edgesToUpdate.push({ id: connector.id, updates: edgeData });
        }
      } catch (err) {
        logger.error('Invalid connector:', connector.id, err);
      }
    });

    // Batch apply all edge additions and updates (1-2 mutations instead of N)
    if (edgesToAdd.length > 0) {
      logger.info('[Yjs→Store] ADD connectors (batch):', { count: edgesToAdd.length });
      batchAddEdges(edgesToAdd, true);
    }
    if (edgesToUpdate.length > 0) {
      logger.info('[Yjs→Store] UPDATE connectors (batch):', { count: edgesToUpdate.length });
      updateEdges(edgesToUpdate, true);
    }

    // Handle deleted connectors
    if (isSynced) {
      syncedConnectorIds.current.forEach(id => {
        if (!currentConnectorIds.has(id)) {
          logger.info('[Yjs→Store] DELETE connector:', { id });
          try { logger.debug('Debug SDI: Deleting connector during ingest', { id }); } catch {}
          deleteEdge(id, true);
          syncedConnectorIds.current.delete(id);
        }
      });
    }

    isSyncingFromYjs.current = false;

    // Only set initial loading to false on the very first sync once we have projected content.
    // Include connectors-only diagrams (e.g. legacy import of a single free line with no shapes).
    if (!hasInitialSyncCompleted.current && (shapes.size > 0 || connectors.size > 0)) {
      hasInitialSyncCompleted.current = true;
      initialSyncTimestamp.current = Date.now();

      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          setInitialLoading(false);
        });
      } else {
        setInitialLoading(false);
      }
    }
  }, [shapes, connectors, diagramType, batchAddNodes, updateNodes, deleteNode, batchAddEdges, updateEdges, deleteEdge, setInitialLoading]);

  // After the settling period, reconcile store positions back to the Y.Doc.
  // During settling, we suppress position overwrites from Yjs→Store to prevent
  // CRDT jitter. But the Y.Doc itself may still hold incorrect positions from
  // late Electric updates. If we don't fix the Y.Doc, the next snapshot push
  // (~30s) will persist those wrong positions permanently.
  // This one-time write-back corrects the Y.Doc to match the React store.
  //
  // Timer is anchored to initialSyncTimestamp (not last shapes change) so that
  // reconciliation always fires AFTER the settling window, even if shapes
  // keep changing from ongoing CRDT updates.
  useEffect(() => {
    if (!hasInitialSyncCompleted.current || positionReconciled.current) return;
    if (!projectionRef.current) return;

    // Target: SETTLE_MS + 1s buffer after initial sync
    const RECONCILE_AT_MS = 4000;
    const elapsed = Date.now() - initialSyncTimestamp.current;
    const delay = Math.max(200, RECONCILE_AT_MS - elapsed);

    const timer = setTimeout(() => {
      if (positionReconciled.current) return;
      positionReconciled.current = true;

      const projection = projectionRef.current;
      if (!projection) return;

      const storeNodes = useDiagramStore.getState().nodes;
      const { mutations } = projection;
      const projShapes = projection.store.getState().shapes;

      // Write store positions → Y.Doc (prevents corrupted snapshot push)
      const ydocFixes: Array<{ id: string; x: number; y: number }> = [];
      for (const node of storeNodes) {
        const shape = projShapes.get(node.id);
        if (!shape) continue;
        if (
          Number.isFinite(node.x)
          && Number.isFinite(node.y)
          && (valueChanged(shape.x, node.x) || valueChanged(shape.y, node.y))
        ) {
          ydocFixes.push({ id: node.id, x: node.x, y: node.y });
        }
      }

      if (ydocFixes.length > 0) {
        logger.info('[Position Reconcile] Writing store positions back to Y.Doc:', {
          count: ydocFixes.length,
          samples: ydocFixes.slice(0, 3).map(f => ({ id: f.id, x: Math.round(f.x), y: Math.round(f.y) })),
        });
        mutations.batchUpdateShapes(
          ydocFixes.map(f => ({ id: f.id, updates: { x: f.x, y: f.y } }))
        );
      } else {
        logger.info('[Position Reconcile] Store and Y.Doc positions match — no fixes needed');
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [shapes]); // Re-run when shapes change to recalculate remaining delay

  // Debounce updates PER NODE to avoid different nodes cancelling each other.
  // Previously a single shared debouncer meant only the last node's update
  // would be sent, which caused multi-select style changes to persist on
  // a single node after the picker was released.
  const debouncedUpdateShapeByNode = useMemo(() => {
    const map = new Map<string, (...args: any[]) => void>();
    return (mutations: any, nodeId: string, updates: any) => {
      let fn = map.get(nodeId);
      if (!fn) {
        fn = debounce((m: any, id: string, u: any) => {
          m.updateShape(id, u);
        }, 50);
        map.set(nodeId, fn);
      }
      fn(mutations, nodeId, updates);
    };
  }, []);

  // Sync Zustand changes back to Yjs via TanStack DB mutations
  useEffect(() => {
    const unsubscribe = useDiagramStore.subscribe((state, prevState) => {
      if (!projectionRef.current) return;
      const ingesting = isSyncingFromYjs.current;

      const { mutations } = projectionRef.current;

      // Handle node changes (dirty-id driven)
      if (state.nodes !== prevState.nodes) {
        isSyncingToYjs.current = true;
        const prevNodeIds = new Set(prevState.nodes.map(n => n.id));
        // const currentNodeIds = new Set(state.nodes.map(n => n.id));

        // Added nodes (only when NOT ingesting from Yjs to avoid echoing Electric-origin content)
        if (!ingesting) {
          state.nodes.forEach(node => {
            if (!prevNodeIds.has(node.id)) {
              const isFreehand = node.type === 'freehand';
              const shapeProps = buildShapePropsFromNode(node);
              const width = node.width ?? node.w;
              const height = node.height ?? node.h;
              mutations.insertShape({
                id: node.id,
                type: node.type || 'person',
                x: node.x,
                y: node.y,
                ...(width !== undefined ? { width } : {}),
                ...(height !== undefined ? { height } : {}),
                zIndex: (node as any).zIndex,
                rotation: (node.attrs as any)?.rotation ?? 0,
                style: {
                  fillColor: node.fillColor,
                  strokeColor: node.strokeColor,
                  strokeWidth: (node as any).strokeWidth,
                },
                // Persist full domain props so imported RDM metadata survives projection round-trips.
                props: isFreehand ? { ...shapeProps, attrs: { ...(node.attrs || {}) } } : shapeProps,
              } as any);
              // Yjs broadcaster automatically broadcasts this change via awareness!
            }
          });
        }

        // Updated nodes - collect changes only for ids that were marked dirty
        const positionUpdates: Array<{ node: DiagramNode; updates: any }> = [];
        const propertyUpdates: Array<{ node: DiagramNode; updates: any }> = [];

        // Consume and clear dirty ids to avoid resending unrelated nodes
        const consumedDirty = useDiagramStore.getState().consumeDirtyIds();
        const dirtyIds = consumedDirty.length > 0 ? consumedDirty : [];
        const prevById = new Map(prevState.nodes.map(n => [n.id, n]));

        if (dirtyIds.length === 0) {
          isSyncingToYjs.current = false;
          return;
        }

        dirtyIds.forEach((id) => {
          const node = state.nodes.find(n => n.id === id);
          if (!node) return;
          const prevNode = prevById.get(id);
          if (prevNode) {
            const geometryPatch = buildFiniteGeometryPatch(prevNode, node);
            const isFreehand = node.type === 'freehand';
            const shapeProps = buildShapePropsFromNode(node);
            const previousShapeProps = buildShapePropsFromNode(prevNode);
            const persistedProps = isFreehand ? { ...shapeProps, attrs: { ...(node.attrs || {}) } } : shapeProps;
            const previousPersistedProps = isFreehand
              ? { ...previousShapeProps, attrs: { ...(prevNode.attrs || {}) } }
              : previousShapeProps;
            const propertyPatch: Record<string, unknown> = {};
            if (valueChanged((prevNode as any).zIndex, (node as any).zIndex)) {
              propertyPatch.zIndex = (node as any).zIndex;
            }
            if (valueChanged((prevNode.attrs as any)?.rotation ?? 0, (node.attrs as any)?.rotation ?? 0)) {
              propertyPatch.rotation = (node.attrs as any)?.rotation ?? 0;
            }
            if (
              valueChanged(prevNode.fillColor, node.fillColor)
              || valueChanged(prevNode.strokeColor, node.strokeColor)
              || valueChanged((prevNode as any).strokeWidth, (node as any).strokeWidth)
            ) {
              propertyPatch.style = {
                fillColor: node.fillColor,
                strokeColor: node.strokeColor,
                strokeWidth: (node as any).strokeWidth,
              };
            }
            if (JSON.stringify(previousPersistedProps) !== JSON.stringify(persistedProps)) {
              // Persist full domain props so imported RDM metadata survives projection round-trips.
              propertyPatch.props = persistedProps;
            }
            const propertyChanged = Object.keys(propertyPatch).length > 0;
            if (Object.keys(geometryPatch).length === 0 && !propertyChanged) return;

            if (Object.keys(geometryPatch).length > 0) {
              // When we are ingesting from Yjs, avoid pushing position changes to prevent thrash
              if (!ingesting) {
                if (ephemeralPreviewIdsRef.current.has(node.id)) return;
                if (state.draggingIds.has(node.id)) return;
                positionUpdates.push({ node, updates: geometryPatch });
              }
            }
            if (propertyChanged) {
              // Property updates (attrs/style) - but avoid feedback loops
              // Only push if NOT ingesting, OR if this shape is pending (means it's a local change we initiated)
              const shouldPushPropertyUpdate = !ingesting || pendingPropertyIdsRef.current.has(node.id);

              // Log color changes from store
              if (prevNode.fillColor !== node.fillColor || prevNode.strokeColor !== node.strokeColor) {
                logger.info('[Store→Yjs] Color change detected:', {
                  id: node.id,
                  from: { fillColor: prevNode.fillColor, strokeColor: prevNode.strokeColor },
                  to: { fillColor: node.fillColor, strokeColor: node.strokeColor },
                  ingesting,
                  isPending: pendingPropertyIdsRef.current.has(node.id),
                  willPush: shouldPushPropertyUpdate
                });
              }

              if (shouldPushPropertyUpdate) {
                propertyUpdates.push({ node, updates: propertyPatch });
              }
            }
          }
        });
        
        // Apply position updates in a single batch transaction
        if (positionUpdates.length > 0) {
          if (positionUpdates.length === 1) {
            // Single update - use regular updateShape
            mutations.updateShape(positionUpdates[0].node.id, positionUpdates[0].updates);
          } else {
            // Multiple updates - use batch update to merge into single Yjs transaction
            const batchUpdates = positionUpdates.map(({ node, updates }) => ({
              id: node.id,
              updates
            }));
            mutations.batchUpdateShapes(batchUpdates);
          }
          // Yjs broadcaster automatically broadcasts this change via awareness!
        }

        if (propertyUpdates.length > 0) {
          // Mark all as pending first to avoid races
          propertyUpdates.forEach(({ node }) => {
            pendingPropertyIdsRef.current.add(node.id);
            try { logger.debug('[Store→Yjs] mark pending', { id: node.id }); } catch {}
          });
          propertyUpdates.forEach(({ node, updates }) => {
            try { logger.debug('[Store→Yjs] updateShape (debounced per node)', { id: node.id, attrs: (updates as any)?.props?.attrs }); } catch {}
            debouncedUpdateShapeByNode(mutations, node.id, updates);
            // Yjs broadcaster automatically broadcasts this change via awareness!
          });
          setTimeout(() => {
            propertyUpdates.forEach(({ node }) => {
              pendingPropertyIdsRef.current.delete(node.id);
              try { logger.debug('[Store→Yjs] clear pending', { id: node.id }); } catch {}
            });
          }, 2000);
        }

        // Deleted nodes
        // prevState.nodes.forEach(node => {
        //   if (!currentNodeIds.has(node.id)) {
        //     logger.info('[Store→Yjs] DELETE shape:', { id: node.id });
        //     mutations.deleteShape(node.id);
        //     // Yjs broadcaster automatically broadcasts this change via awareness!
        //   }
        // });
        
        isSyncingToYjs.current = false;
      }

      // Handle edge changes
      if (state.edges !== prevState.edges) {
        isSyncingToYjs.current = true;
        const prevEdgeIds = new Set(prevState.edges.map(e => e.id));
        // const currentEdgeIds = new Set(state.edges.map(e => e.id));

        // Added edges (only when NOT ingesting from Yjs to avoid echoing Electric-origin content)
        if (!ingesting) {
          state.edges.forEach(edge => {
            if (!prevEdgeIds.has(edge.id)) {
              logger.info('[Store→Yjs] INSERT connector:', {
                id: edge.id,
                type: edge.type,
                from: edge.from,
                to: edge.to,
                attrs: edge.attrs
              });
              mutations.insertConnector({
                id: edge.id,
                type: edge.type,
                sourceShapeId: edge.from,
                targetShapeId: edge.to,
                // Store edge attributes under props.attrs to match reader/hydrator
                props: {
                  attrs: {
                    ...(edge.attrs || {}),
                  },
                  // include anchors/offsets for persistence
                  fromAnchor: (edge as any).fromAnchor,
                  toAnchor: (edge as any).toAnchor,
                  fromOffset: (edge as any).fromOffset,
                  toOffset: (edge as any).toOffset,
                },
              });
              // Yjs broadcaster automatically broadcasts this change via awareness!
            }
          });
        }

        // Updated edges (type/props) - push updates for existing ids
        state.edges.forEach(edge => {
          if (prevEdgeIds.has(edge.id)) {
            // Avoid spamming Yjs with per-move drag previews for free lines.
            if (useDiagramStore.getState().draggingIds.has(edge.id)) return;

            const prev = prevState.edges.find(e => e.id === edge.id)!;
            if (JSON.stringify(edge.attrs) !== JSON.stringify(prev.attrs) || edge.type !== prev.type) {
              mutations.updateConnector(edge.id, {
                type: edge.type,
                // Persist under props.attrs to avoid losing flags on reload
                props: {
                  attrs: {
                    ...(edge.attrs || {}),
                  },
                  fromAnchor: (edge as any).fromAnchor,
                  toAnchor: (edge as any).toAnchor,
                  fromOffset: (edge as any).fromOffset,
                  toOffset: (edge as any).toOffset,
                }
              });
              // Yjs broadcaster automatically broadcasts this change via awareness!
            }
          }
        });

        // Deleted edges
        // prevState.edges.forEach(edge => {
        //   if (!currentEdgeIds.has(edge.id)) {
        //     logger.info('[Store→Yjs] DELETE connector:', { id: edge.id });
        //     mutations.deleteConnector(edge.id);
        //     // Yjs broadcaster automatically broadcasts this change via awareness!
        //   }
        // });
        
        isSyncingToYjs.current = false;
      }
    });

    return unsubscribe;
  }, [debouncedUpdateShapeByNode]);

  return { 
    doc, 
    awareness,
    provider,
    projection: projectionRef.current,
  };
}
